import axios from "axios";
import { NGROK } from "../../../../APIs";

export interface BloodHoundQuery {
  id?: number;
  description: string;
  query: string;
  includeProperties?: boolean;
}

export interface ADNodeData {
  label: string;
  kind: string;
  kinds?: string[];
  objectId: string;
  isTierZero: boolean;
  isOwnedObject: boolean;
  lastSeen: string;
  properties: Record<string, any>;
}

export interface ADEdgeData {
  source: string;
  target: string;
  label: string;
  kind: string;
  lastSeen: string;
  properties: Record<string, any>;
}

export interface BloodHoundQueryResponse {
  data: {
    nodes: Record<string, ADNodeData>;
    edges: ADEdgeData[];
  };
}

export interface TenantData {
  tenantName: string;
  [key: string]: any;
}

// ============= API Calls =============

export const fetchTenants = async (): Promise<TenantData[]> => {
  const response = await axios.get(`${NGROK}/api/get-all-domains`, {

  });
  return response.data || [];
};

export const fetchBloodHoundQueries = async (tenantName: string): Promise<BloodHoundQuery[]> => {
  const response = await axios.get(
    `${NGROK}/api/bloodhound/query/get-all`,
    {

      params: { tenantName },
    }
  );
  return response.data || [];
};

export const runBloodHoundQuery = async (
  cypherQuery: string,
  tenantName: string,
  includeProperties?: boolean,
  signal?: AbortSignal
): Promise<BloodHoundQueryResponse> => {
  const response = await axios.post(
    `${NGROK}/api/bloodhound/query/run`,
    { cypherQuery, includeProperties, tenantName },
    { signal },
  );
  return response.data;
};

export interface BloodHoundEntityInfo {
  props: Record<string, any>;
  kinds: string[];
}

export interface BloodHoundRelatedEntity {
  objectID?: string;
  name?: string;
  label?: string;
  kind?: string;
  props?: Record<string, any>;
}

export interface BloodHoundPaginatedResponse<T> {
  count: number;
  skip: number;
  limit: number;
  data: T[];
}

export interface BloodHoundAssetGroupTag {
  id: number;
  name: string;
  type: number;
}

const BLOODHOUND_URL = `${NGROK}/api/bloodhound`;

export const fetchEntityInfo = async (
  tenantName: string,
  entityType: string,
  objectId: string,
  signal?: AbortSignal,
): Promise<BloodHoundEntityInfo> => {
  const response = await axios.get(`${BLOODHOUND_URL}/entities/${entityType}`, {
    params: { tenantName, objectId, counts: false },
    signal,
  });
  return response.data?.data ?? { props: {}, kinds: [] };
};

export const fetchEntityRelationship = async (
  tenantName: string,
  entityType: string,
  objectId: string,
  relationship: string,
  options: { skip?: number; limit?: number; signal?: AbortSignal } = {},
): Promise<BloodHoundPaginatedResponse<BloodHoundRelatedEntity>> => {
  const response = await axios.get(`${BLOODHOUND_URL}/entities/${entityType}/${relationship}`, {
    params: { tenantName, objectId, skip: options.skip ?? 0, limit: options.limit ?? 128 },
    signal: options.signal,
  });
  return {
    count: response.data?.count ?? 0,
    skip: response.data?.skip ?? 0,
    limit: response.data?.limit ?? 0,
    data: response.data?.data ?? [],
  };
};

export const fetchEntityRelationshipGraph = async (
  tenantName: string,
  entityType: string,
  objectId: string,
  relationship: string,
  signal?: AbortSignal,
): Promise<BloodHoundQueryResponse> => {
  const response = await axios.get(`${BLOODHOUND_URL}/entities/${entityType}/${relationship}`, {
    params: { tenantName, objectId, type: "graph" },
    signal,
  });
  return response.data;
};

export const fetchNodeGraph = async (
  tenantName: string,
  objectId: string,
  signal?: AbortSignal,
): Promise<BloodHoundQueryResponse> => {
  const response = await axios.get(`${BLOODHOUND_URL}/graph/search`, {
    params: { tenantName, query: objectId, type: "exact" },
    signal,
  });
  return response.data;
};

export const fetchAssetGroupTags = async (tenantName: string): Promise<BloodHoundAssetGroupTag[]> => {
  const response = await axios.get(`${BLOODHOUND_URL}/asset-group-tags`, {
    params: { tenantName, counts: false },
  });
  return response.data?.data?.tags ?? [];
};
